/**
 * Cases tab — Jess's case files (INV-####) in whose-court columns, with the
 * filters Scott asked for on the left (2026-09-08: "I'll clean up categories
 * on the left so you can filter by those things too" — on us / waiting on
 * customer / ready to close, plus Investigating / Remediating / Completed).
 *
 * Scott's round-2 asks are baked in: the progress bar is labelled with what
 * it counts ("Checklist: 3 of 5 done"), asks live on their case (the violet
 * chip lights when an answer needs review), and each column says what it
 * means. A card click opens the case's workspace at its own URL (Phase 2) —
 * the slide-over panel that used to duplicate the case file is retired.
 *
 * Waiting filter round: "Show" narrows to the cases waiting on someone else
 * (their court, or an ask still open) or the ones someone has answered on,
 * and "Waiting on" to one person. Every filter lives in the URL query, so a
 * link and the back button keep it; an opened case's previous / next walk
 * the filtered board.
 *
 * Proposal 5 (kanban 59162c19): the court comes from the asks (the server's
 * `court`), each card names who and how long, columns run oldest first,
 * completed cases live under Done only, and every count reads caseBoard.
 */
import { useMemo, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FILTER_CHIPS,
  STATUS_LABEL,
  filterCount,
  formatInvestigationDate,
  unseenResponseLabel,
} from '../../components/portal/investigationsView';
import type { DoorInvestigationRead } from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { boardOrder, caseBoard, caseColumns, checklistProgress } from './qualityTriageView';
import { COURT_CHIPS, applyCourtFilter } from '../../components/portal/caseCourtView';
import CaseCourtLines from './CaseCourtLines';
import FilterChip from './FilterChip';
import StateWordPill from './StateWordPill';
import { answeredWord, askWord, isPastDue, type StateWord } from './stateWords';
import AskSentWord from './AskSentWord';
import {
  DEFAULT_CASE_FILTERS,
  applyCaseFilters,
  asksForCase,
  caseFiltersActive,
  caseWorkspacePath,
  fromTab,
  waitingOnNames,
  waitingOnPeople,
  threadWorkspacePath,
  type CaseFilters,
  type WaitingRow,
} from './qualityWorkspaceView';
import { useQualityTriage } from './qualityTriageContext';
import { caseWaitsOn, waitingSince } from './caseWaitingView';
import {
  namesInclude,
  peopleWaitedOn,
  readListFilter,
  withListFilter,
  type ListFilter,
} from './listFilter';
import {
  newestUnseenAnswer,
  noticeForCase,
  respondedLine,
  type ItemResponse,
} from './respondedView';
import type { DoorThreadRead } from '../../services/qcpInvestigations';
import { latestNoteLine } from '../../components/portal/noteBoxView';
import {
  clockStatusLine,
  effectivenessClock,
  isOverdueCase,
  overdueLine,
} from '../../components/portal/slaClocksView';
import { SkipToItems } from './SkipToItems';
import { primaryRef } from './caseIdentity';
import styles from './QualityTriagePages.module.css';

function CaseCard({
  investigation,
  onOpen,
  asks = [],
  response = null,
}: {
  investigation: DoorInvestigationRead;
  onOpen: (inv: DoorInvestigationRead) => void;
  /** The asks that belong to this case (for "Waiting on …"). */
  asks?: DoorDataRequestRead[];
  /** Someone answered on it and nobody has opened the answer yet. */
  response?: ItemResponse | null;
}) {
  const progress = checklistProgress(investigation);
  // Named when the asks say who answered; the bare server count otherwise.
  const answer = response ? null : unseenResponseLabel(investigation.unseen_response_count);
  const waitingOn = waitingOnNames(asks);
  const note = latestNoteLine(investigation.latest_note, investigation.note_count ?? 0);
  // Sigma clocks (kanban 6c648cab): a case past a deadline says so on its card.
  const overdue = overdueLine(investigation);
  // The stage-3 watch on a Waiting-on-effectiveness card: "watching · 166d
  // left" (kanban d91f705b), from the same clock the case file shows.
  const watch = investigation.court.side === 'watching' ? effectivenessClock(investigation) : null;
  return (
    <button
      type="button"
      className={`${styles.caseCard} ${response ? styles.caseCardResponded : ''}`}
      onClick={() => onOpen(investigation)}
      data-door-item={investigation.investigation_no}
      data-action="qcp_item_open"
      data-target-case-id={investigation.investigation_no}
    >
      <span className={styles.caseId}>
        {primaryRef(null, investigation).label} · {STATUS_LABEL[investigation.status]}
      </span>
      <span className={styles.caseTitle}>{investigation.title}</span>
      <CaseCourtLines investigation={investigation} waitingOn={waitingOn} />
      {note && (
        <span className={styles.rowNote} data-testid="case-note">
          📝 {note}
        </span>
      )}
      {response && (
        <span className={styles.respondedLine} data-testid="case-responded">
          ✓ {respondedLine(response)} — review
        </span>
      )}
      {answer && <span className={styles.chipAsk}>💬 {answer} — review</span>}
      {overdue && (
        <span className={styles.chipOverdue} data-testid="case-overdue">
          {overdue}
        </span>
      )}
      {watch && (
        <span className={styles.chipWatch} data-testid="case-watch">
          ⏱ {clockStatusLine(watch)}
        </span>
      )}
      {progress && (
        <>
          <span className={styles.checkLabel}>
            Checklist: {progress.done} of {progress.total} done
          </span>
          <span className={styles.progress}>
            <span
              className={styles.progressFill}
              style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
            />
          </span>
        </>
      )}
      <span className={styles.caseMeta}>
        updated {formatInvestigationDate(investigation.updated_at)}
      </span>
    </button>
  );
}

/** When the door last added to an ask's thread (turn 3: a follow-up to Jake
 *  left his row reading "asked 1h ago", as if nothing had been sent). */
function lastFollowUp(r: DoorDataRequestRead): string | null {
  const times = (r.messages ?? []).map((m) => m.created_at).filter((t) => t > r.created_at);
  return times.length ? times.sort()[times.length - 1] : null;
}

/** Everyone you have asked something, across cases, and whether they answered. */
function WaitingPanel({
  rows,
  threadFor,
}: {
  rows: WaitingRow[];
  /** The item an ask with no case was asked from, when one is tracked. */
  threadFor: (r: DoorDataRequestRead) => DoorThreadRead | null;
}) {
  const navigate = useNavigate();
  return (
    <section className={styles.waitPanel} aria-label="Waiting on people">
      <h3 className={styles.waitHead}>
        Waiting on people
        {rows.length > 0 && <span className={styles.lanePill}>{rows.length}</span>}
      </h3>
      {rows.length === 0 ? (
        <p className={styles.emptyNote}>No questions are out with anyone.</p>
      ) : (
        <ul className={styles.waitList}>
          {rows.map(({ request: r, investigation: inv }) => (
            <li
              key={r.id}
              className={styles.waitRow}
              // The whole row opens where the ask lives (turn 5 clicked the
              // name and nothing happened); the links inside still work.
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('a')) return;
                const item = inv ? null : threadFor(r);
                if (inv) navigate(`${caseWorkspacePath(inv, 'tab')}#case-asks`, fromTab('cases'));
                else if (item) navigate(threadWorkspacePath(item, 'tab'));
                else if (r.conversation_id)
                  navigate(`../ask?conversation=${encodeURIComponent(r.conversation_id)}`);
              }}
              style={{ cursor: inv || threadFor(r) || r.conversation_id ? 'pointer' : undefined }}
            >
              <span className={styles.waitWho}>{r.target_name}</span>
              <span className={styles.waitWhat} title={r.question_title}>
                {r.question_title}
              </span>
              <span className={styles.waitWhen}>
                <AskSentWord request={r} />
                {r.status === 'answered' ? (
                  // Unread already says "answered · unread" (AskSentWord);
                  // read says it in words too, not only in green (proposal 10).
                  !r.answer_unseen && <StateWordPill word={answeredWord(false)} />
                ) : (
                  <>
                    asked {formatInvestigationDate(r.created_at)}
                    {lastFollowUp(r) &&
                      ` · you followed up ${formatInvestigationDate(lastFollowUp(r) as string)}`}
                    {r.next_followup_at &&
                      ` · reminder ${new Date(r.next_followup_at).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })}`}
                    {isPastDue(r.next_followup_at, new Date()) && (
                      <>
                        {' '}
                        <StateWordPill word={askWord([r]) as StateWord} />
                      </>
                    )}
                  </>
                )}
              </span>
              {inv ? (
                <Link
                  className={styles.waitCase}
                  to={`${caseWorkspacePath(inv, 'tab')}#case-asks`}
                  state={fromTab('cases').state}
                >
                  {primaryRef(null, inv).label}
                </Link>
              ) : threadFor(r) ? (
                // Asked from an item that is not a case yet (waiting filter
                // round: it read "no case · chat" though the item was known).
                <Link className={styles.waitCase} to={threadWorkspacePath(threadFor(r) as DoorThreadRead, 'tab')}>
                  {primaryRef(threadFor(r), null).label}
                </Link>
              ) : r.conversation_id ? (
                <Link
                  className={styles.waitCase}
                  to={`../ask?conversation=${encodeURIComponent(r.conversation_id)}`}
                  title="Not on a case. Opens the chat it was asked from."
                >
                  no case · chat
                </Link>
              ) : (
                <span className={styles.waitCase}>no case</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const COURTS = new Set<string>(COURT_CHIPS.map((c) => c.key));
// Closed cases are not on this board (they live under Done), so the
// Completed stage is not a filter here; Waiting on effectiveness (a
// completed case still in its watch, kanban d91f705b) is.
const STAGE_CHIPS = FILTER_CHIPS.filter((c) => c.key !== 'completed');
const STAGES = new Set<string>(STAGE_CHIPS.map((c) => c.key));

/** Court and stage from the URL (unknown values fall back to the default). */
function readCaseFilters(params: URLSearchParams): CaseFilters {
  const court = params.get('court') ?? '';
  const stage = params.get('stage') ?? '';
  return {
    court: (COURTS.has(court) ? court : DEFAULT_CASE_FILTERS.court) as CaseFilters['court'],
    stage: (STAGES.has(stage) ? stage : DEFAULT_CASE_FILTERS.stage) as CaseFilters['stage'],
  };
}

export default function QualityCasesPage() {
  const { invState, dataRequests, responses = [] } = useQualityTriage();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requests = useMemo(() => dataRequests?.requests ?? [], [dataRequests?.requests]);
  const waitingRows = useMemo(
    () => waitingOnPeople(requests, invState.investigations, invState.threads),
    [requests, invState.investigations, invState.threads],
  );
  const asksOf = (inv: DoorInvestigationRead) => {
    const thread = invState.threads.find((t) => t.investigation_id === inv.id) ?? null;
    return asksForCase(requests, inv, thread);
  };
  const threadFor = (r: DoorDataRequestRead) =>
    r.conversation_id ? (invState.threads.find((t) => t.conversation_id === r.conversation_id) ?? null) : null;

  const filters = readCaseFilters(params);
  const show = readListFilter(params);
  const setQuery = (next: URLSearchParams) => setParams(next);
  const setFilters = (f: CaseFilters) => {
    const next = new URLSearchParams(params);
    next.delete('court');
    next.delete('stage');
    if (f.court !== DEFAULT_CASE_FILTERS.court) next.set('court', f.court);
    if (f.stage !== DEFAULT_CASE_FILTERS.stage) next.set('stage', f.stage);
    setQuery(next);
  };
  const setShow = (f: ListFilter) => setQuery(new URLSearchParams(withListFilter(params, f)));

  // Who each case waits on, and who answered on it (newest unread answer:
  // named from its asks, or the server's bare count when the asks are not
  // readable here).
  const waitsOn = (inv: DoorInvestigationRead) => caseWaitsOn(inv, asksOf(inv));
  const responseOf = (inv: DoorInvestigationRead): ItemResponse | null => {
    const named = noticeForCase(responses, inv)?.response ?? newestUnseenAnswer(asksOf(inv));
    return named;
  };
  const hasAnswer = (inv: DoorInvestigationRead) =>
    responseOf(inv) !== null || inv.unseen_response_count > 0;

  // The board's one list (open cases, oldest first): the tab badge, these
  // rail counts and the column counts all start from it (proposal 5).
  const board = caseBoard(invState.investigations);
  const byCourtStage = applyCaseFilters(board, filters);
  const waitingCases = byCourtStage.filter((inv) => waitsOn(inv) !== null);
  const people = peopleWaitedOn(waitingCases.map((inv) => waitsOn(inv) ?? []));
  const answeredCases = byCourtStage.filter(hasAnswer);
  // Sigma clocks: a case with a clock or a dated item past due (the server
  // judges both; the card says which).
  const overdueCases = byCourtStage.filter(isOverdueCase);
  // Inside the filters: answered first (the one thing to act on), then the
  // one waiting longest.
  const answeredAt = (inv: DoorInvestigationRead) => {
    const r = responseOf(inv);
    const t = r ? new Date(r.at).getTime() : NaN;
    return Number.isNaN(t) ? 0 : t;
  };
  const filtered =
    show.show === 'waiting'
      ? waitingCases
          .filter((inv) => !show.who || namesInclude(waitsOn(inv) ?? [], show.who))
          .sort(
            (a, b) =>
              Number(hasAnswer(b)) - Number(hasAnswer(a)) || waitingSince(a) - waitingSince(b),
          )
      : show.show === 'responded'
        ? [...answeredCases].sort((a, b) => answeredAt(b) - answeredAt(a))
        : show.show === 'overdue'
          ? overdueCases
          : byCourtStage;
  // Columns keep the board's order: oldest first.
  const cols = caseColumns(filtered);
  const narrowed = caseFiltersActive(filters) || show.show !== 'all';

  // Column by column, as the board reads: an opened case offers previous /
  // next through them (case round, finding 6), filtered as shown.
  const order = boardOrder(cols).map((i) => i.investigation_no);
  const search = params.toString() ? `?${params.toString()}` : '';
  const openCase = (inv: DoorInvestigationRead) =>
    navigate(caseWorkspacePath(inv, 'tab'), fromTab('cases', order, search));

  const column = (
    title: string,
    sub: string,
    empty: string,
    cases: DoorInvestigationRead[],
    foot?: ReactNode,
  ) => (
    <div className={styles.caseCol} data-testid="case-column" data-title={title}>
      <h3 className={styles.caseColTitle}>
        {title}
        <span className={styles.lanePill} data-testid="case-column-count">
          {cases.length}
        </span>
      </h3>
      <p className={styles.caseColSub}>{sub}</p>
      {cases.length === 0 && <p className={styles.emptyNote}>{empty}</p>}
      {cases.map((inv) => (
        <CaseCard
          key={inv.id}
          investigation={inv}
          onOpen={openCase}
          asks={asksOf(inv)}
          response={responseOf(inv)}
        />
      ))}
      {foot}
    </div>
  );

  // With a court picked, only that column has anything to say — show it
  // alone rather than two empty neighbours.
  const showOurs = filters.court === 'any' || filters.court === 'ours';
  const showTheirs = filters.court === 'any' || filters.court === 'theirs';
  const showReady = filters.court === 'any' || filters.court === 'ready';
  const showWatching = filters.court === 'any' || filters.court === 'watching';

  const showChip = (label: string, count: number, on: boolean, f: ListFilter) => (
    <FilterChip
      key={label}
      label={label}
      count={count}
      on={on}
      action="qcp_cases_filter"
      recordValue="show"
      value={f.who ? 'waiting_on_person' : f.show}
      onClick={() => setShow(f)}
    />
  );

  return (
    <div className={styles.page}>
      <SkipToItems list="cases" />
      {dataRequests?.available !== false && dataRequests && (
        <WaitingPanel rows={waitingRows} threadFor={threadFor} />
      )}
      <div className={styles.casesLayout}>
        <aside className={styles.filterRail} aria-label="Case filters">
          <div className={styles.filterGroup}>
            <div className={styles.filterHead}>Show</div>
            {showChip('All cases', byCourtStage.length, show.show === 'all', { show: 'all', who: null })}
            {showChip('Waiting on others', waitingCases.length, show.show === 'waiting' && !show.who, {
              show: 'waiting',
              who: null,
            })}
            {showChip('Responded', answeredCases.length, show.show === 'responded', {
              show: 'responded',
              who: null,
            })}
            {showChip('Overdue', overdueCases.length, show.show === 'overdue', {
              show: 'overdue',
              who: null,
            })}
          </div>
          {people.length > 1 && (
            <div className={styles.filterGroup}>
              <div className={styles.filterHead}>Waiting on</div>
              {people.map((p) =>
                showChip(p.who, p.count, !!show.who && namesInclude([p.who], show.who), {
                  show: 'waiting',
                  who: p.who,
                }),
              )}
            </div>
          )}
          <div className={styles.filterGroup}>
            <div className={styles.filterHead}>Whose court</div>
            {COURT_CHIPS.map((chip) => (
              <FilterChip
                key={chip.key}
                label={chip.label}
                count={
                  applyCourtFilter(
                    applyCaseFilters(board, { court: 'any', stage: filters.stage }),
                    chip.key,
                  ).length
                }
                on={filters.court === chip.key}
                action="qcp_cases_filter"
                recordValue="court"
                value={chip.key}
                onClick={() => setFilters({ ...filters, court: chip.key })}
              />
            ))}
          </div>
          <div className={styles.filterGroup}>
            <div className={styles.filterHead}>Stage</div>
            {STAGE_CHIPS.map((chip) => (
              <FilterChip
                key={chip.key}
                label={chip.label}
                count={filterCount(applyCaseFilters(board, { court: filters.court, stage: 'all' }), chip.key)}
                on={filters.stage === chip.key}
                action="qcp_cases_filter"
                recordValue="stage"
                value={chip.key}
                onClick={() => setFilters({ ...filters, stage: chip.key })}
              />
            ))}
          </div>
          {narrowed && (
            <button
              type="button"
              className={styles.filterClear}
              onClick={() => setQuery(new URLSearchParams())}
            >
              Clear filters
            </button>
          )}
        </aside>
        <div
          className={styles.caseCols}
          data-single={!showOurs || !showTheirs || !showReady || !showWatching || undefined}
        >
          {showOurs &&
            column(
              'Our court',
              'No ask is out with anyone: the next step is ours. Oldest first.',
              'Nothing is on us right now.',
              cols.ours,
            )}
          {showTheirs &&
            column(
              'Their court',
              'An ask is open with the person named on the card. Oldest first.',
              'Nobody owes us an answer right now.',
              cols.theirs,
            )}
          {showReady &&
            column(
              'Ready to close',
              "Everything's done — just needs your sign-off",
              'Nothing here needs a sign-off.',
              cols.ready,
            )}
          {showWatching &&
            column(
              'Waiting on effectiveness',
              'Fixed on our end; watching for a repeat on the same cause until the watch ends. Oldest first.',
              'No case is waiting on its effectiveness watch.',
              cols.watching,
              <p className={styles.emptyNote} data-testid="cases-completed-note">
                A case moves to <Link to="../done">Done</Link> on its own when its watch runs out.
              </p>,
            )}
          {narrowed && filtered.length === 0 && (
            <p className={styles.emptyNote}>No case matches those filters.</p>
          )}
        </div>
      </div>
    </div>
  );
}
